import { CheckOutlined, ContentCopy, ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Stack,
	Typography
} from '@mui/material';
import CodeViewer from 'components/CodeViewer';
import React, { useCallback, useMemo } from 'react';
import { AuditDetailKey } from 'types/api';
import { formatAuditDetail } from 'utils/auditUtils';
import { useCopyToClipboard } from 'usehooks-ts';

interface IAuditDetailBlockProps {
	name: AuditDetailKey;
	value: string;
	defaultExpanded?: boolean;
}

const AuditDetailBlock: React.FC<IAuditDetailBlockProps> = ({
	name,
	value,
	defaultExpanded = false
}) => {
	const [copiedText, copy] = useCopyToClipboard();
	const { text, language } = useMemo(() => formatAuditDetail(value), [value]);

	const copyText = useCallback(async () => {
		await copy(text);
	}, [copy, text]);

	return (
		<Accordion
			variant="outlined"
			disableGutters
			defaultExpanded={defaultExpanded}
			slotProps={{ transition: { unmountOnExit: true } }}
			data-testid={`audit-detail-${name}`}
		>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Typography variant="subtitle2">
					{intl.tr('audit.drawer.detail', { name })}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Stack gap={1}>
					<Stack direction="row" justifyContent="flex-end">
						<Button
							size="small"
							color="inherit"
							data-testid={`audit-detail-copy-${name}`}
							startIcon={
								copiedText === text ? (
									<CheckOutlined data-testid="copy-success-icon" />
								) : (
									<ContentCopy />
								)
							}
							onClick={copyText}
						>
							{intl.tr('audit.drawer.copy')}
						</Button>
					</Stack>
					<CodeViewer
						data-testid={`audit-detail-content-${name}`}
						content={text}
						language={language}
						maxHeight={400}
					/>
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
};

export default AuditDetailBlock;
